import React, { useEffect, useState } from 'react';
import { CalendarDays, Minus, Plus, ShoppingBag, Trash2 } from 'lucide-react';
import { useScrollToTop } from '../hooks/useScrollToTop';
import { CartItem, useCart } from '../contexts/CartContext';
import PaymentModal, { getPeekPassCheckoutTotal, PaymentMethod } from './PaymentModal';
import Header from './Header';
import { getFullImageUrl } from '../services/api';
import { getCurrentToken, getCurrentUser } from '../utils/userStorage';
import {
  applyCheckoutUpdatesToCart,
  CheckoutAuthRequiredError,
  CheckoutRevalidationError,
  createMercadoPagoPreference,
  redirectToCheckoutAuth,
  summarizeCheckoutChanges
} from '../utils/checkoutFlow';

interface CartViewProps {
  onGoBack: () => void;
}

const formatPrice = (price: number, currency: string) => new Intl.NumberFormat('es-AR', {
  style: 'currency',
  currency,
  maximumFractionDigits: 2
}).format(price);

const getResolvedImage = (image?: string) => {
  if (!image) return '';
  return /^(https?:|data:|blob:)/i.test(image) ? image : getFullImageUrl(image);
};

const CartView: React.FC<CartViewProps> = ({ onGoBack }) => {
  const { state, removeFromCart, replaceCartItems, updateQuantity } = useCart();
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  useScrollToTop();

  const currency = state.items[0]?.currency || 'ARS';
  const subtotal = state.total;
  const checkout = getPeekPassCheckoutTotal(subtotal, state.items);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('checkout') === '1' && state.items.length) {
      setShowPaymentModal(true);
      window.history.replaceState({}, document.title, '/cart');
    }
  }, [state.items.length]);

  const requireAuthAndReturnToCheckout = () => {
    redirectToCheckoutAuth({
      type: 'cart-checkout',
      returnTo: '/cart?checkout=1'
    });
  };

  const handleContinueToPayment = () => {
    setPaymentError(null);
    if (!getCurrentUser() || !getCurrentToken()) {
      requireAuthAndReturnToCheckout();
      return;
    }
    setShowPaymentModal(true);
  };

  const handlePaymentMethodSelect = async (method: PaymentMethod) => {
    if (method.id !== 'mercadopago') return;
    setPaymentError(null);

    try {
      const result = await createMercadoPagoPreference(state.items, {
        externalReferencePrefix: 'peekpass_cart',
        organizerId: state.items[0]?.organizerId || null,
        courseId: state.items[0]?.courseId || state.items[0]?.eventId || null
      });

      const paymentUrl = result.payment_url || result.init_point;
      if (!paymentUrl) throw new Error('Mercado Pago no devolvió una URL de pago.');
      window.location.href = paymentUrl;
    } catch (error: any) {
      setShowPaymentModal(false);

      if (error instanceof CheckoutAuthRequiredError) {
        requireAuthAndReturnToCheckout();
        return;
      }

      if (error instanceof CheckoutRevalidationError) {
        const updatedItems = applyCheckoutUpdatesToCart(state.items, error.result);
        replaceCartItems(updatedItems);
        const changes = summarizeCheckoutChanges(error.result);

        if (updatedItems.length && window.confirm(`${changes}\n\nActualizamos tu carrito. ¿Querés confirmar los cambios y continuar con Mercado Pago?`)) {
          try {
            const result = await createMercadoPagoPreference(updatedItems, {
              acceptChanges: true,
              externalReferencePrefix: 'peekpass_cart',
              organizerId: updatedItems[0]?.organizerId || null,
              courseId: updatedItems[0]?.courseId || updatedItems[0]?.eventId || null
            });
            const paymentUrl = result.payment_url || result.init_point;
            if (!paymentUrl) throw new Error('Mercado Pago no devolvió una URL de pago.');
            window.location.href = paymentUrl;
            return;
          } catch (retryError: any) {
            setPaymentError(retryError.message || 'No pudimos iniciar el pago. Intentá nuevamente.');
            return;
          }
        }

        setPaymentError(updatedItems.length ? 'Revisá el carrito actualizado antes de continuar.' : changes);
        return;
      }

      setPaymentError(error.message || 'No pudimos iniciar el pago. Intentá nuevamente.');
    }
  };

  if (!state.items.length) {
    return (
      <div className="min-h-screen bg-[#f8f5ef] text-slate-950">
        <Header showBackButton onBack={onGoBack} title="Tus reservas" />
        <main className="grid min-h-[70vh] place-items-center px-4 py-12">
          <section className="max-w-md border border-slate-200 bg-white p-9 text-center shadow-[0_24px_58px_-38px_rgba(15,23,42,0.38)]">
            <ShoppingBag className="mx-auto h-11 w-11 text-[#71931b]" />
            <h1 className="mt-5 text-3xl font-black tracking-[-0.05em]">Tu reserva está vacía</h1>
            <p className="mt-3 text-sm leading-6 text-slate-600">Elegí una fecha y sumá tus entradas para continuar.</p>
            <button type="button" onClick={onGoBack} className="mt-7 bg-black px-5 py-3 text-sm font-black text-white transition hover:bg-[#536d12]">Explorar eventos</button>
          </section>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] w-full max-w-full overflow-x-hidden bg-[#f8f5ef] text-slate-950">
      <Header showBackButton onBack={onGoBack} title="Tus reservas" />
      <main className="w-full max-w-full px-3 pb-[calc(7rem+env(safe-area-inset-bottom))] pt-5 sm:px-6 sm:py-8 lg:px-8 lg:py-10">
        <div className="mx-auto grid min-w-0 max-w-[1440px] gap-5 sm:gap-8 xl:grid-cols-[minmax(0,1fr)_340px]">
          <section className="min-w-0">
            <div className="mb-7 flex flex-col justify-between gap-3 border-b border-slate-200 pb-5 sm:flex-row sm:items-end">
              <div><p className="text-xs font-black uppercase tracking-[0.22em] text-[#71931b]">Resumen de la reserva</p><h1 className="mt-2 text-3xl font-black tracking-[-0.06em]">Entradas seleccionadas</h1></div>
              <p className="text-sm font-semibold text-slate-600">{state.itemCount} {state.itemCount === 1 ? 'entrada' : 'entradas'}</p>
            </div>
            <div className="space-y-4">
              {state.items.map((item: CartItem) => {
                const quantity = item.quantity || 1;
                return (
                  <article key={item.id} className="grid min-w-0 gap-4 overflow-hidden rounded-xl border border-slate-200 bg-white p-3 shadow-[0_16px_34px_-30px_rgba(15,23,42,0.28)] sm:grid-cols-[132px_minmax(0,1fr)_auto] sm:rounded-none sm:p-5">
                    <div className="relative h-32 overflow-hidden bg-slate-200 sm:h-full sm:min-h-[142px]">
                      {item.image ? <img src={getResolvedImage(item.image)} alt={item.title} className="h-full w-full object-cover" /> : <TicketPlaceholder />}
                      {item.eventDate ? <div className="absolute bottom-0 left-0 bg-black px-3 py-2 text-xs font-black text-white">{item.eventDate}</div> : null}
                    </div>
                    <div className="min-w-0 py-1">
                      <p className="text-[10px] font-black uppercase tracking-[0.18em] text-[#71931b]">{item.category}</p>
                      <h2 className="mt-2 text-xl font-black tracking-[-0.045em]">{item.title}</h2>
                      {item.ticketTierName ? <p className="mt-1 text-sm font-bold text-slate-700">{item.ticketTierName}</p> : null}
                      <p className="mt-2 flex items-center gap-2 text-sm font-semibold text-slate-700"><CalendarDays className="h-4 w-4 text-[#71931b]" />{item.eventDate || 'Fecha a confirmar'}</p>
                      <p className="mt-1 text-sm text-slate-600">{item.venue || item.instructor}</p>
                      <p className="mt-4 text-sm font-bold">{formatPrice(item.price, item.currency)} <span className="font-normal text-slate-500">por entrada</span></p>
                    </div>
                    <div className="flex items-end justify-between gap-4 border-t border-slate-100 pt-4 sm:flex-col sm:items-end sm:border-l sm:border-t-0 sm:pl-5 sm:pt-1">
                      <div className="inline-flex items-center border border-slate-300 bg-[#fcfbf8] p-1">
                        <button type="button" onClick={() => updateQuantity(item.id, quantity - 1)} disabled={quantity <= 1} aria-label="Restar una entrada" className="grid h-9 w-9 place-items-center transition hover:bg-[#eef8ca] disabled:cursor-not-allowed disabled:opacity-30"><Minus className="h-4 w-4" /></button>
                        <span className="w-9 text-center text-sm font-black">{quantity}</span>
                        <button type="button" onClick={() => updateQuantity(item.id, quantity + 1)} aria-label="Sumar una entrada" className="grid h-9 w-9 place-items-center transition hover:bg-[#eef8ca]"><Plus className="h-4 w-4" /></button>
                      </div>
                      <div className="text-right"><p className="text-lg font-black">{formatPrice(item.price * quantity, item.currency)}</p><button type="button" onClick={() => removeFromCart(item.id)} className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold text-slate-500 transition hover:text-red-700"><Trash2 className="h-3.5 w-3.5" />Quitar</button></div>
                    </div>
                  </article>
                );
              })}
            </div>
          </section>

          <aside className="h-fit border border-slate-200 bg-white p-6 shadow-[0_22px_48px_-36px_rgba(15,23,42,0.4)] xl:sticky xl:top-28">
            <p className="text-xs font-black uppercase tracking-[0.22em] text-[#71931b]">Total de compra</p>
            <div className="mt-6 space-y-3 text-sm"><div className="flex justify-between gap-4"><span className="text-slate-600">Valor de la entrada</span><span className="font-bold">{formatPrice(checkout.subtotal, currency)}</span></div><div className="flex justify-between gap-4"><span className="text-slate-600">Cargo por servicio</span><span className="font-bold">{formatPrice(checkout.fee, currency)}</span></div><div className="flex items-end justify-between gap-4 border-t border-slate-200 pt-4"><span className="text-base font-black">Total a pagar</span><span className="text-2xl font-black tracking-[-0.05em]">{formatPrice(checkout.total, currency)}</span></div></div>
            {paymentError ? <p className="mt-5 border-l-2 border-red-600 bg-red-50 px-3 py-2 text-xs leading-5 text-red-700">{paymentError}</p> : null}
            <button type="button" onClick={handleContinueToPayment} className="mt-7 w-full bg-black px-5 py-4 text-sm font-black text-white transition hover:bg-[#536d12]">Continuar al pago</button>
            <p className="mt-4 text-center text-xs leading-5 text-slate-500">Vas a confirmar los detalles y seguir con Mercado Pago.</p>
          </aside>
        </div>
      </main>
      <PaymentModal isOpen={showPaymentModal} onClose={() => setShowPaymentModal(false)} onPaymentMethodSelect={handlePaymentMethodSelect} total={subtotal} currency={currency} itemCount={state.itemCount} items={state.items} onUpdateQuantity={updateQuantity} />
    </div>
  );
};

const TicketPlaceholder = () => <div className="grid h-full place-items-center bg-[#eef8ca] text-xs font-black uppercase tracking-[0.18em] text-[#536d12]">PEEK PASS.</div>;

export default CartView;
